import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LNGS } from "@/i18n";
import { isSkipPath } from "@/i18n/wave_d1/glms_locales";

/**
 * Reads the locale from the URL path prefix (`/it/*`, `/es/*`, ...) and
 * syncs i18next to it. This is what makes locale-aware SEO SSR routes
 * work end-to-end: when a crawler hits `/it/pricing`, the middleware
 * serves Italian raw HTML AND the React app on hydration also speaks
 * Italian instead of falling back to the user's persisted language.
 *
 * On paths WITHOUT a locale prefix (e.g. `/pricing`), we don't touch
 * i18next — we let the user's persisted `glms_lang` choice stand.
 *
 * Phase VI.5 · Wave C · Hotfix R1 · short-circuit sui skip-path
 * (`/sa/*`, `/admin/*`, `/app/*`, ...) · evita falsi match su token
 * di 2 lettere come "sa" (Sanskrit).
 */
export default function LocaleSync() {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();

    useEffect(() => {
        // Wave C R1 · skip-path short-circuit prima del regex match.
        if (isSkipPath(pathname)) return;
        // Match either `/zh-CN/...` (5-char) or `/xx/...` (2-char).
        const m = pathname.match(/^\/(zh-CN|[a-z]{2})(?=\/|$)/);
        if (!m) return;
        const candidate = m[1];
        if (!SUPPORTED_LNGS.includes(candidate)) return;
        const currentShort = (i18n.language || "").split("-")[0];
        const target = candidate === "zh-CN" ? "zh-CN" : candidate;
        const currentIsMatch = target === "zh-CN"
            ? i18n.language === "zh-CN"
            : currentShort === candidate;
        if (currentIsMatch) return;
        i18n.changeLanguage(target);
    }, [pathname, i18n]);

    return null;
}
